import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, Flame, Music2, Maximize2, X, Youtube, Music } from "lucide-react";
import { api } from "../lib/api";

export default function PartyMode() {
  const [board, setBoard] = useState({ youtube: [], spotify: [] });
  const [nowPlaying, setNowPlaying] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/song-requests/board").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/song-requests/now-playing").then((r) => setNowPlaying(r.data)).catch(() => {});
    api.get("/song-requests/history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const top = useMemo(() => [...(board.youtube || []), ...(board.spotify || [])].sort((a, b) => b.count - a.count).slice(0, 8), [board]);
  const recent = history.filter((h) => h.url_key !== nowPlaying?.url_key);

  const toggleFs = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  return (
    <div className="min-h-[100dvh] bg-[#0D0B14] text-white overflow-hidden relative" data-testid="party-mode">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: "radial-gradient(circle at 18% 18%, rgba(157,78,221,0.20), transparent 45%), radial-gradient(circle at 85% 80%, rgba(212,175,55,0.16), transparent 45%)" }} />
      <div className="relative z-10 p-6 lg:p-10 h-[100dvh] flex flex-col">
        <div className="flex items-center justify-between mb-6 lg:mb-9">
          <div>
            <p className="text-[#D4AF37] tracking-[0.3em] text-xs lg:text-sm uppercase">Darkchata</p>
            <h1 className="font-display text-3xl lg:text-5xl font-bold">Song Requests</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleFs} data-testid="party-fullscreen" className="w-11 h-11 rounded-full bg-white/8 border border-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"><Maximize2 size={18} /></button>
            <Link to="/dj-board" data-testid="party-exit" className="w-11 h-11 rounded-full bg-white/8 border border-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"><X size={18} /></Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 flex-1 min-h-0">
          <div className="lg:col-span-2 flex flex-col min-h-0">
            <p className="text-[#A19DB0] uppercase tracking-widest text-xs lg:text-sm mb-3">Now spinning</p>
            {nowPlaying ? (
              <div data-testid="party-now-playing" className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.14] to-[#9D4EDD]/[0.10] p-8 lg:p-10 flex-1 flex flex-col justify-center">
                <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-6"><Disc3 size={64} className="text-[#D4AF37] animate-spin" style={{ animationDuration: "4s" }} /></div>
                <p className="font-display text-3xl lg:text-5xl font-bold leading-tight break-words" data-testid="party-now-playing-title">{nowPlaying.title || nowPlaying.url}</p>
                <p className="text-[#A19DB0] mt-3 capitalize">{nowPlaying.platform}</p>
              </div>
            ) : (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 flex-1 flex items-center justify-center text-[#A19DB0]">Waiting for the DJ…</div>
            )}
            {recent.length > 0 && (
              <div className="mt-5">
                <p className="text-[#A19DB0] uppercase tracking-widest text-[10px] lg:text-xs mb-2">Just played</p>
                <div className="flex flex-wrap gap-2" data-testid="party-history">
                  {recent.slice(0, 4).map((h) => (
                    <span key={h.url_key} className="text-xs lg:text-sm px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#A19DB0] flex items-center gap-1.5 max-w-[240px] truncate"><Music2 size={12} className="shrink-0" />{h.title || h.url}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-3 flex flex-col min-h-0">
            <p className="text-[#A19DB0] uppercase tracking-widest text-xs lg:text-sm mb-3">Top requests</p>
            {top.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] flex-1 flex items-center justify-center text-[#A19DB0] text-center px-6">No requests yet — open Request a Song to get the floor going!</div>
            ) : (
              <div className="space-y-2 lg:space-y-3 overflow-hidden" data-testid="party-top-list">
                {top.map((s, i) => (
                  <div key={s.url_key} data-testid={`party-song-${i}`} className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-3 lg:py-4" style={i === 0 ? { borderColor: "#D4AF3766", background: "rgba(212,175,55,0.10)" } : undefined}>
                    <span className="font-display text-2xl lg:text-4xl font-bold w-10 lg:w-14 text-center shrink-0" style={{ color: i === 0 ? "#D4AF37" : "#6b6580" }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-lg lg:text-2xl font-semibold truncate">{s.title || s.url}</p>
                      <p className="text-[#A19DB0] text-xs lg:text-sm capitalize flex items-center gap-1.5">{s.platform === "youtube" ? <Youtube size={13} /> : <Music size={13} />}{s.platform}</p>
                    </div>
                    <span className="flex items-center gap-2 text-xl lg:text-3xl font-bold text-[#FF7A45] shrink-0"><Flame size={22} /> {s.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
